import { useEffect, useRef } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useOrgId } from '@/app/session';
import { Button, ErrorState, LoadingState } from '@/components/ui';
import { formatDateTime } from '@/lib/format';
import { messagesQuery, type Conversation, type Message } from './api';
import { ChatTimeline } from './ChatTimeline';
import { Composer } from './Composer';
import { shouldContinueMessages } from './thread-read-state';

export function recordedKind(kind: string | null | undefined): string {
  return kind === null || kind === undefined ? 'Recorded entry · source kind unknown' : `Recorded ${kind}`;
}

/** Source recorder labels are not email authors or native actors. Never split or
 * infer headers, signatures, addresses or recipient controls from this body. */
export function RecordedMessage({ message }: { message: Message }) {
  const source = message.source_record;
  if (!source) return null;
  return <article className="msg msg-recorded" id={`message-${message.id}`}>
    <div className="msg-main">
      <header className="msg-head"><h3>{recordedKind(source.kind)}</h3></header>
      <p>Source recorder: {source.author === null ? 'Unknown' : source.author === '' ? '(empty source label)' : source.author}</p>
      <p>{source.occurred_at ? <>Event date: <time dateTime={source.occurred_at}>{formatDateTime(source.occurred_at)}</time></> : 'Event date unknown'}</p>
      <p>{source.source_created_at ? <>Source entry created: <time dateTime={source.source_created_at}>{formatDateTime(source.source_created_at)}</time></> : 'Source entry creation date unknown'}</p>
      {source.content_restricted || source.tombstone ? <p>Source-deleted content is restricted.</p> : <pre className="msg-text" style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>{source.body}</pre>}
      <p><a href={`/app/companies/${encodeURIComponent(source.company_id)}`}>Linked company</a>{source.location_id ? <> · <a href={`?site=${encodeURIComponent(source.location_id)}`}>Linked site</a></> : ' · No source site'}</p>
      <details><summary>Source record details</summary>
        <p>Recorded history, not provider-authenticated mail. No delivery or RFC thread is asserted.</p>
        <p>Source: {source.source_system} / {source.source_instance} / {source.external_id}</p>
        <p>Native technical recorder: {message.recorded_by_actor_id}</p>
        <p>Source revision: {source.source_revision}</p>
        {source.edited_at ? <p>Source entry edited: {formatDateTime(source.edited_at)} · {source.edited_by ?? 'Unknown editor'}</p> : null}
        {source.deleted_at ? <p>Source entry deleted: {formatDateTime(source.deleted_at)} · {source.deleted_by ?? 'Unknown deleter'}</p> : null}
        <p>Original and file references: restricted evidence. No attachment download.</p>
      </details>
    </div>
  </article>;
}

/** Source records stay inert; the human composer uses the existing native reply bridge. */
export function RecordedThread({ conversation }: { conversation: Conversation }) {
  const orgId = useOrgId();
  const query = useInfiniteQuery(messagesQuery(orgId, conversation.id));
  const { hasNextPage, isFetchingNextPage, isError, isPending, fetchNextPage } = query;
  useEffect(() => {
    if (shouldContinueMessages({ hasNextPage, isFetchingNextPage, isError, isPending })) void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isError, isPending, fetchNextPage]);
  const contextReady = query.isSuccess && !query.isError && !query.hasNextPage && !query.isFetching;
  const scrollRef = useRef<HTMLElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const scrolledConversation = useRef<string | null>(null);
  useEffect(() => {
    // Reset even when the reused route is still loading its next conversation.
    if (scrolledConversation.current !== conversation.id) scrolledConversation.current = null;
    if (!contextReady || scrolledConversation.current === conversation.id || !endRef.current || !scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    scrolledConversation.current = conversation.id;
  }, [conversation.id, contextReady]);
  const history = conversation.source_history;
  const name = history ? history.company_name || 'Recorded company history' : conversation.counterpart?.name || conversation.counterpart?.email || conversation.title;
  return <div className="thread-pane"><section className="thread" ref={scrollRef} aria-label={history ? 'Recorded company history' : 'Recorded email conversation'}>
    <header className="thread-head"><h2>{name}</h2></header>
    <div className="thread-body">
    <p>{history ? 'History / Notes' : 'Email'} · Original records unchanged · Automation paused</p>
    {history ? <a href={`/app/companies/${encodeURIComponent(history.company_id)}`}>Open company</a> : null}
    {query.isPending ? <LoadingState label="Loading recorded correspondence" /> : null}
    {query.isError ? <ErrorState error={query.error} onRetry={() => void (query.isFetchNextPageError ? query.fetchNextPage() : query.refetch())} /> : null}
    <ChatTimeline messages={query.data?.pages.flatMap(page => page.items) ?? []} />
    {query.hasNextPage ? <Button disabled={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>More recorded entries</Button> : null}
    <div className="thread-reply"><Composer conversation={conversation} messages={query.data?.pages.flatMap(page => page.items) ?? []} contextReady={contextReady} /></div>
    <div ref={endRef} data-chat-end="" aria-hidden="true" />
    </div>
  </section></div>;
}
